import {Box, Flex, Divider, Spacer, Heading} from '@chakra-ui/react';
import BoxCard from '../../components/General/BoxCard';
import LinkButton from '../../components/General/LinkButton';
import {CustomRadioGroup, CustomSelect, SubmitButton} from '../../components/General/FormInputs';
import React, {useState, useEffect, useRef} from 'react';
import FormCodes from '../../constants/FormCodes';
import CriteriaCard from '../../components/Reports/CriteriaCard';
import ErrorHelper from '../../service/ErrorHelper';
import LoadingWheel from '../../components/General/LoadingWheel';
import {Report} from '../../service/Urls';
import {get, getReport} from '../../service/ApiService';
import {useForm} from 'react-hook-form';
import {CustomInput, DatePicker} from '../../components/General/FormInputs';
import Visibility from '../../constants/Visibility';
import {LocationSelect} from '../../components/Location/LocationSelect';
import {ArrangerSelect} from '../../components/Arranger/ArrangerSelect';
import {GroupSelect} from '../../components/Group/GroupSelect';
import {ClientSelect} from '../../components/Client/ClientSelect';
import FlexCard from '../../components/General/FlexCard';
import SurveyTypesReports from '../../components/Reports/SurveyTypesReports';
import {DepartmentSelect} from '../../components/Department/DepartmentSelect';
import {useTitle} from '../../components/General/Layout';

const OpportunityFinancialReports = () => {
  // use setTitle to update the title of the page, otherwise set in the router typically
  const [title, setTitle] = useTitle('');

  // current selected report
  const [reportVal, setReportVal] = useState('');

  // current selected radio button id, needed for radio control
  const [promptId, setPromptId] = useState('');

  // list of report data
  const [sectionList, setSectionsList] = useState('');

  // current sectionId
  const [sectionId, setSectionId] = useState('');

  // current service types to show
  const [serviceTypes, setServiceTypes] = useState('');

  // currently selected service type
  const [serviceTypeValue, setServiceTypeValue] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [isInitialLoading, setInitialLoading] = useState(true);
  const [toast, setToast] = useState({});
  const [pdfUrl, setPdfUrl] = useState('');
  const linkRef = useRef(null);

  // for the report options
  const radios = {
    needs: [
      {key: 'At Need', value: 'atNeed'},
      {key: 'PreNeed', value: 'preNeed'},
      {key: 'Both', value: 'both'},
    ],
    preNeedRevenue: [
      {key: 'Funeral', value: 'funeral'},
      {key: 'Cremation', value: 'cremation'},
      {key: 'Both', value: 'both'},
    ],
  };

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    loadData();
  }, []);

  // once the pdf url is loaded click the hidden link to open a new tab
  useEffect(() => {
    if (pdfUrl) linkRef.current.click();
  }, [pdfUrl]);

  useEffect(() => {
    if (Array.isArray(serviceTypes)) {
      if (serviceTypes.includes(serviceTypeValue)) {
        return;
      } else {
        setServiceTypeValue(serviceTypes[0]);
      }
    }
  }, [serviceTypes]);

  useEffect(() => {
    if (Array.isArray(sectionList)) {
      sectionList.forEach(section => {
        const obj = section.reports.find(obj => {
          return obj.promptId === promptId;
        });

        if (typeof obj !== 'undefined') {
          setReportVal(obj);
        }
      });
    }
  }, [promptId]);

  useEffect(() => {
    defaultDates();
  }, [reportVal]);

  const defaultDates = () => {
    const formValues = getValues();
    const Today = new Date().toISOString().split('T')[0];
    if (reportVal.hasOwnProperty('startDateState')) setValue('startDate', formValues.startDate || Today);
    if (reportVal.hasOwnProperty('startDate2State')) setValue('startDate2', formValues.startDate2 || Today);
    if (reportVal.hasOwnProperty('endDateState')) setValue('endDate', formValues.endDate || Today);
    if (reportVal.hasOwnProperty('endDate2State')) setValue('endDate2', formValues.endDate2 || Today);
  };

  const filterFields = data => {
    if (!reportVal.hasOwnProperty('startDateState')) data.startDate = '';
    if (!reportVal.hasOwnProperty('startDate2State')) data.startDate2 = '';
    if (!reportVal.hasOwnProperty('endDateState')) data.endDate = '';
    if (!reportVal.hasOwnProperty('endDate2State')) data.endDate2 = '';
    if (!reportVal.hasOwnProperty('atPreNeedState')) data.atPreNeed = '';
    if (!reportVal.hasOwnProperty('questionState')) data.question = '';
    if (!reportVal.hasOwnProperty('preNeedRevenueChartState')) data.preNeedRevenue = '';
    if (!reportVal.hasOwnProperty('numMosDashboardState')) data.monthsForData = '';
  };

  const loadData = async () => {
    const data = await get(Report.LIST + `/` + FormCodes.OPPORTUNITY_PLAN);
    setToast(data);
    if (!data.error) {
      const sections = data.data;
      if (sections) {
        setPromptId(sections[0].reports[0].promptId);
        setSectionsList(sections);
        setSectionId(sections[0].sectionId);
        setReportVal(sections[0]?.reports[0]);
        setServiceTypes(sections[0]?.reports[0]?.serviceTypes);
      }
    }
    setLoading(false);
    setInitialLoading(false);
    setTitle('Opportunity Plan');
  };

  const ReportCards = () => {
    const col1 = [];
    const col2 = [];
    for (let i = 0; i < sectionList.length; i++) {
      const sec = sectionList[i];
      if (i < 2) {
        col1.push(
          <CriteriaCard value={promptId} setValue={setPromptId} reportInfo={sec} key={sec.sectionId} setSectionId={setSectionId}>
            reports
          </CriteriaCard>
        );
      } else {
        col2.push(
          <CriteriaCard value={promptId} setValue={setPromptId} reportInfo={sec} key={sec.sectionId} setSectionId={setSectionId}>
            reports
          </CriteriaCard>
        );
      }
    }
    return (
      <>
        <Box m="3">{col1}</Box>
        <Box m="3">
          {col2}
          {/* Service Types */}
          <Box>
            <FlexCard>
              <SurveyTypesReports serviceTypeValue={serviceTypeValue} setServiceTypeValue={setServiceTypeValue} serviceTypes={serviceTypes} />
            </FlexCard>
          </Box>
        </Box>
      </>
    );
  };

  const submit = async data => {
    setLoading(true);
    data.formCode = FormCodes.OPPORTUNITY_PLAN;
    data.sectionId = sectionId;
    data.promptId = reportVal.promptId;
    data.serviceType = serviceTypeValue;
    if (data.importAnalysis) data[data.importAnalysis] = true;
    filterFields(data);

    const reportName = await getReport(data);

    if (reportName) {
      if (reportName.error) {
        setToast(reportName);
      } else {
        setPdfUrl(reportName);
      }
    }
    setLoading(false);
  };

  return (
    <>
      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" ref={linkRef} style={{display: 'none'}} />
      {isInitialLoading ? (
        <LoadingWheel />
      ) : (
        <form onSubmit={handleSubmit(submit)}>
          <Flex>
            {/* Sections */}
            <ReportCards />

            {/* Report Options */}
            <Box m="3" textAlign={'start'}>
              <BoxCard>
                <Heading>Report Options</Heading>
                <Divider my="2" />
                <Flex>
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Starting Date"
                    id="startDate"
                    required={reportVal?.startDateState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('startDateState')}
                  />
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Starting Date 2"
                    id="startDate2"
                    required={reportVal?.startDate2State === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('startDate2State')}
                  />
                </Flex>
                <Flex>
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Ending Date"
                    id="endDate"
                    required={reportVal?.startDateState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('endDateState')}
                  />
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Ending Date 2"
                    id="endDate2"
                    required={reportVal?.endDate2State === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('endDate2State')}
                  />
                </Flex>

                <Divider my="2" />
                <CustomRadioGroup
                  list={radios.needs}
                  control={control}
                  errors={errors}
                  id="radioNeeds"
                  defaultValue={!reportVal.hasOwnProperty('atPreNeedState') ? '0' : 'both'}
                  required={reportVal?.atPreNeedState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('atPreNeedState')}
                />

                <Divider my="2" />
                <ClientSelect
                  register={register}
                  errors={errors}
                  id={'clientCode'}
                  required={reportVal?.clientIdState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('clientIdState')}
                  setRHFValue={setValue}
                />

                <Divider my="2" />
                <DepartmentSelect control={control} name={'emDept'} />

                <Divider my="2" />
                <GroupSelect
                  register={register}
                  errors={errors}
                  id={'groupId'}
                  required={reportVal?.groupState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('groupState')}
                  setRHFValue={setValue}
                  setId
                />

                <Divider my="2" />
                <LocationSelect
                  register={register}
                  errors={errors}
                  id={'locationID'}
                  required={reportVal?.locationState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('locationState')}
                  setRHFValue={setValue}
                  clientId={watch('clientId')}
                />

                <Divider my="2" />

                <ArrangerSelect
                  register={register}
                  errors={errors}
                  id={'arrangerID'}
                  required={reportVal?.arrangerState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('arrangerState')}
                  setRHFValue={setValue}
                  clientId={watch('clientId')}
                />

                <Divider my="2" />
                <CustomSelect id="region" name="Region" register={register} errors={errors} />

                <Divider my="2" />
                <Box textAlign={'center'}>
                  <Heading size="md">PreNeed Revenue Training</Heading>
                  <CustomRadioGroup
                    list={radios.preNeedRevenue}
                    control={control}
                    errors={errors}
                    id="preNeedRevenue"
                    defaultValue={!reportVal.hasOwnProperty('preNeedRevenueChartState') ? '0' : 'display'}
                    required={reportVal?.preNeedRevenueChartState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('preNeedRevenueChartState')}
                  />
                </Box>

                <Divider my="2" />
                <CustomInput
                  id="monthsForDashboard"
                  register={register}
                  errors={errors}
                  name="No. of Months for Dashboard Report"
                  required={reportVal?.numMosDashboardState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('numMosDashboardState')}
                />

                <Divider my="2" />
                {isLoading ? (
                  <Flex justify={'center'}>
                    <LoadingWheel />
                  </Flex>
                ) : (
                  <>
                    <Flex>
                      <Box w="45%">
                        <LinkButton to="/" label="Back" />
                      </Box>
                      <Spacer />
                      <Box w="45%">
                        <SubmitButton name="Print" />
                      </Box>
                    </Flex>
                  </>
                )}
              </BoxCard>
            </Box>
          </Flex>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}
    </>
  );
};

export default OpportunityFinancialReports;
